import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeQuestion, setActiveQuestion] = useState<number | null>(null);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Add Schema.org JSON-LD
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#website`,
          "url": import.meta.env.VITE_SITE_URL || 'https://example.com',
          "name": "Optimus",
          "description": "Data processing for human beings",
          "publisher": {
            "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#organization`
          }
        },
        {
          "@type": "Organization",
          "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#organization`,
          "name": "Optimus",
          "url": import.meta.env.VITE_SITE_URL || 'https://example.com',
          "logo": {
            "@type": "ImageObject",
            "url": "/images/logo.gif"
          },
          "sameAs": [
            "https://twitter.com/hi_optimus",
            "https://github.com/ironmussa/Optimus",
            "https://medium.com/hi-optimus"
          ]
        },
        {
          "@type": "WebPage",
          "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#webpage`,
          "url": import.meta.env.VITE_SITE_URL || 'https://example.com',
          "name": "Optimus - Data Processing for Human Beings",
          "isPartOf": {
            "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#website`
          },
          "about": {
            "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#organization`
          },
          "description": "Easily write code to clean, transform, explore and visualize data using Python. Open-source data processing library."
        },
        {
          "@type": "SoftwareApplication",
          "name": "Optimus",
          "operatingSystem": "Cross-platform",
          "applicationCategory": "DeveloperApplication",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "ratingCount": "150"
          }
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How is Optimus different from Pandas, Dask, cuDF, etc.?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Think Optimus as a universal way to access many of the dataframe technologies available in python. Optimus can works with Pandas, Dask, Spark, Vaex, cuDF, and Dask-cudf as backend."
              }
            },
            {
              "@type": "Question",
              "name": "Why so many data processing engines?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Although most dataframe API tries to mimic Pandas there are always little differences in the way these dataframes work. With Optimus, we want to let you code and then use the technology and infrastructure available to you to process your data."
              }
            },
            {
              "@type": "Question",
              "name": "How can Optimus use CPUs and GPUs?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "For CPU, Optimus can use Pandas, Dask, Spark, or Vaex. For GPUs, Optimus relies on cuDF and Dask-cuDF."
              }
            },
            {
              "@type": "Question",
              "name": "Not sure if I understand between Pandas, Dask, cuDF... and Optimus. Can you explain further?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Optimus focused on give you the best tools for all your data processing needs. From data quality, plotting, parsing dates, URLs, email, and NLP preparation. Optimus give you the best performance, so you don't have to reinvent the wheel."
              }
            }
          ]
        }
      ]
    });
    document.head.appendChild(schemaScript);

    return () => {
      document.head.removeChild(schemaScript);
    };
  }, []);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      
      const response = await fetch('https://readdy.ai/api/form/d5e726mfmrgp9cvlvobg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });
      
      if (response.ok) {
        setSubmitMessage('Thank you for subscribing!');
        setEmail('');
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 3000);
    }
  };

  const toggleQuestion = (index: number) => {
    setActiveQuestion(activeQuestion === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-[#E8F5E9] font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <h1 className="flex items-center gap-2">
                <img 
                  src="/images/logo.gif" 
                  alt="Optimus Logo" 
                  className="h-8"
                />
                <span className="text-xl font-semibold text-gray-900">Optimus</span>
              </h1>
            </Link>
            
            <div className="hidden md:flex items-center gap-4">
              <a href="https://github.com/hi-primus/optimus/discussions" target="_blank" rel="noopener noreferrer nofollow" className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">
                Community
              </a>
              <a href="https://github.com/ironmussa/optimus" target="_blank" rel="noopener noreferrer nofollow" className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">
                Github
              </a>
              <a href="https://medium.com/hi-optimus" target="_blank" rel="noopener noreferrer nofollow" className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">
                Blog
              </a>
              <a href="https://www.amazon.com/Data-Processing-Optimus-Supercharge-preparation/dp/1801079560" target="_blank" rel="noopener noreferrer nofollow" className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">
                Book
              </a>
            </div>

            <button className="md:hidden p-2 cursor-pointer">
              <i className="ri-menu-line text-2xl text-gray-900"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                <strong>Data processing</strong> for human beings
              </h2>
              <p className="text-lg text-gray-700 mb-8">
                Easily write code to <strong>clean, transform, explore and visualize data</strong> using python.
              </p>
              <a 
                href="https://github.com/hi-primus/optimus" 
                target="_blank" 
                rel="noopener noreferrer nofollow"
                className="inline-block px-8 py-4 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                Install Optimus now! 🚀
              </a>
            </div>
            <div className="flex justify-center">
              <img 
                src="/images/hero.webp" 
                alt="Optimus Data Processing Illustration" 
                title="Optimus Python Data Processing"
                className="w-full max-w-md"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Get Started Fast Section */}
      <section className="py-16 px-6 bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-8">Get started fast:</h2>
          <div className="bg-gray-800 rounded-lg p-6 relative">
            <pre className="text-green-400 font-mono text-lg">pip install pyoptimus</pre>
            <button 
              onClick={() => {
                navigator.clipboard.writeText('pip install pyoptimus');
              }}
              className="absolute top-4 right-4 px-4 py-2 bg-teal-500 text-white rounded-md hover:bg-teal-600 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
            >
              Copy Code
            </button>
          </div>
        </div>
      </section>

      {/* Book CTA Section */}
      <section className="py-20 px-6 bg-[#E8F5E9]">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-4xl font-bold text-gray-900 mb-4">
            Get the <strong>Optimus Book</strong>, written by its creators.📕
          </h3>
          <p className="text-lg text-gray-700 mb-8">
            This book explains how to load, prepare and join data at any scale. It is written by its creators who use this methodology in their day-to-day work.
          </p>
          <a 
            href="https://www.amazon.com/-/es/Dr-Argenis-Leon-ebook-dp-B093XS4JHF/dp/B093XS4JHF/ref=mt_other?_encoding=UTF8&me=&qid=1628683187" 
            target="_blank" 
            rel="noopener noreferrer nofollow"
            className="inline-block px-8 py-4 bg-teal-500 text-white font-medium rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Purchase on Amazon
          </a>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-4xl font-bold text-gray-900 text-center mb-16">
            What can <strong>Optimus</strong> do for you?
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-teal-100 rounded-full">
                <i className="ri-edit-line text-3xl text-teal-600"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3"><a href="#easy-api">Easy to write and easy to read.</a></h4>
              <p className="text-gray-600">
                Not tech-savvy? not a problem! Write transformation instruction in plain English.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-teal-100 rounded-full">
                <i className="ri-database-2-line text-3xl text-teal-600"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3"><a href="#connect-files">No matter your data size.</a></h4>
              <p className="text-gray-600">
                Process a small dataset on your laptop or use a cluster to process Big data. You can use Pandas, Dask, cuDF, Dask-cuDF, Vaex or Spark to process your data.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-teal-100 rounded-full">
                <i className="ri-open-source-line text-3xl text-teal-600"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3"><a href="#open-source">Open-source</a></h4>
              <p className="text-gray-600">
                Download and use it anywhere. (Apache 2.0 License)
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Easy API Section */}
      <section id="easy-api" className="py-20 px-6 bg-[#E8F5E9]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold text-gray-900 mb-4"><strong>Easy API</strong></h3>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto">
              In a little more than 10 lines you can, remove white spaces, accents in all columns, lowercase all columns data, drop a "dummyCol", transform date format, sort a column, convert integers to a "string", replace "taco" per "taaaccoo" and "pizza" per "pizzza"
            </p>
          </div>
          <div className="w-full max-w-4xl mx-auto bg-gray-900 rounded-lg p-8 shadow-lg">
            <pre className="text-sm text-green-400 font-mono overflow-x-auto">
              <code>{`# This is a custom function
def func(value, arg):
    return "this was a number"

new_df = df\\
    .rows.sort("rank", "desc")\\
    .cols.copy("age", "new_age")\\
    .cols.lower(["names","function"])\\
    .cols.date_transform("date arrival", "yyyy/MM/dd", "dd-MM-YYYY")\\
    .cols.years_between("date arrival", "dd-MM-YYYY", output_cols = "from arrival")\\
    .cols.remove_accents("names")\\
    .cols.remove_special_chars("names")\\
    .rows.drop(df["rank"]>8)\\
    .cols.rename(str.lower)\\
    .cols.trim("*")\\
    .cols.unnest("japanese name", output_cols="other names")\\
    .cols.unnest("last position seen", separator=",", output_cols="pos")\\
    .cols.drop(["last position seen", "japanese name","date arrival", "cybertronian", "nulltype"])`}</code>
            </pre>
          </div>
        </div>
      </section>

      {/* Connect to Files Section */}
      <section id="connect-files" className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold text-gray-900 mb-4"><strong>Connect to files and databases</strong></h3>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto">
              Load and save locally (or remotely) Excel, CSV, JSON, parquet, Avro. Get and insert data from Mysql, Redshift, SQL Server, Postgres, Oracle, Casandra, and Presto.
            </p>
          </div>
          <div className="w-full max-w-4xl mx-auto bg-gray-900 rounded-lg p-8 shadow-lg">
            <pre className="text-sm text-green-400 font-mono overflow-x-auto">
              <code>{`# Put your db credentials here
db = op.connect(
    driver="mysql",
    host="165.227.196.70",
    database="optimus",
    user="test",
    password="test")

# Convert a table a dataframe
df = db.table_to_df("test_data")`}</code>
            </pre>
          </div>
        </div>
      </section>

      {/* Partners Logos */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-3 md:grid-cols-5 gap-8 items-center justify-items-center">
            <img src="/images/partner-1.gif" alt="Partner Logo" title="Technology Partner" className="h-12 object-contain" />
            <img src="/images/partner-2.gif" alt="Partner Logo" title="Technology Partner" className="h-16 object-contain" />
            <img src="/images/partner-3.gif" alt="Partner Logo" title="Technology Partner" className="h-8 object-contain" />
            <img src="/images/partner-4.gif" alt="Partner Logo" title="Technology Partner" className="h-12 object-contain" />
            <img src="/images/partner-5.gif" alt="Partner Logo" title="Technology Partner" className="h-12 object-contain" />
            <img src="/images/partner-6.gif" alt="Partner Logo" title="Technology Partner" className="h-10 object-contain" />
            <img src="/images/partner-7.gif" alt="Partner Logo" title="Technology Partner" className="h-10 object-contain" />
            <img src="/images/partner-8.gif" alt="Partner Logo" title="Technology Partner" className="h-10 object-contain" />
            <img src="/images/partner-9.gif" alt="Partner Logo" title="Technology Partner" className="h-12 object-contain" />
          </div>
        </div>
      </section>

      {/* Advanced Features */}
      <section className="py-20 px-6 bg-[#E8F5E9]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4"><strong>Advanced features</strong></h3>
            <p className="text-lg text-gray-700">All you need to handle your data in one place.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
              <h4 className="text-xl font-bold text-gray-900 mb-3">Outlier Detection</h4>
              <p className="text-gray-600">Easily detect outlier with Out of box functions.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
              <h4 className="text-xl font-bold text-gray-900 mb-3">Machine Learning</h4>
              <p className="text-gray-600">Apply linear regression, logistic regression or K-means easily.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
              <h4 className="text-xl font-bold text-gray-900 mb-3">String Clustering</h4>
              <p className="text-gray-600">Cluster similar strings and change it for single value.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
              <h4 className="text-xl font-bold text-gray-900 mb-3">NLP functions</h4>
              <p className="text-gray-600">Stem and Lemmatize verbs, Tokenize strings, word count, remove diacritics, expand contratect words and more.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Used By Companies */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Used by <strong>Forward thinking companies</strong></h3>
            <p className="text-lg text-gray-700">Here are a few of our favorites!</p>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-12">
            <img src="/images/company-1.gif" alt="Company Logo" title="Company using Optimus" className="h-10 object-contain" />
            <img src="/images/company-2.gif" alt="Company Logo" title="Company using Optimus" className="h-6 object-contain" />
            <img src="/images/company-3.gif" alt="Company Logo" title="Company using Optimus" className="h-10 object-contain" />
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 px-6 bg-[#E8F5E9]">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-4xl font-bold text-gray-900 text-center mb-12">What People Say</h3>
          <div className="bg-white rounded-lg p-8 shadow-sm">
            <img src="/images/bbva-logo.gif" alt="BBVA Logo" title="BBVA Testimonial" className="h-8 mb-6" />
            <p className="text-lg text-gray-700 italic">
              "The group of BBVA Data &amp; Analytics in Mexico has been using Optimus for the past months, and we have boosted our performance for cleansing, exploring and analyzing our data by 10x factor."
            </p>
          </div>
        </div>
      </section>

      {/* Featured On */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-4xl font-bold text-gray-900 text-center mb-12">Featured On</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center justify-items-center">
            <a href="https://www.datasciencecentral.com/profiles/blogs/data-cleansing-with-apache-spark-and-optimus" target="_blank" rel="noopener noreferrer nofollow">
              <img src="/images/featured-1.gif" alt="Data Science Central" title="Featured on Data Science Central" className="h-14 object-contain" />
            </a>
            <a href="https://towardsdatascience.com/announcing-optimus-v2-agile-data-science-workflows-made-easy-c127a12d9e13" target="_blank" rel="noopener noreferrer nofollow">
              <img src="/images/featured-2.gif" alt="Towards Data Science" title="Featured on Towards Data Science" className="h-6 object-contain" />
            </a>
            <a href="https://www.kdnuggets.com/2018/08/optimus-v2-agile-data-science-workflows-made-easy.html" target="_blank" rel="noopener noreferrer nofollow">
              <img src="/images/featured-3.gif" alt="KDnuggets" title="Featured on KDnuggets" className="h-14 object-contain" />
            </a>
            <a href="https://www.bbvadata.com/cleansing-exploratory-data-analysis-apache-spark-optimus/" target="_blank" rel="noopener noreferrer nofollow">
              <img src="/images/featured-4.gif" alt="BBVA Data" title="Featured on BBVA Data" className="h-12 object-contain" />
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 bg-[#E8F5E9]">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-4xl font-bold text-gray-900 text-center mb-12">F.A.Q.</h3>
          <div className="space-y-4">
            <div className="bg-white rounded-lg overflow-hidden">
              <button
                onClick={() => toggleQuestion(0)}
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <h4 className="text-lg font-semibold text-teal-600">How is Optimus different from Pandas, Dask, cuDF, etc.?</h4>
                <i className={`ri-arrow-${activeQuestion === 0 ? 'up' : 'down'}-s-line text-2xl text-gray-400`}></i>
              </button>
              {activeQuestion === 0 && (
                <div className="px-6 pb-4">
                  <p className="text-gray-700">
                    Think Optimus as a universal way to access many of the dataframe technologies available in python. Optimus can works with Pandas, Dask, Spark, Vaex, cuDF, and Dask-cudf as backend.
                  </p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg overflow-hidden">
              <button
                onClick={() => toggleQuestion(1)}
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <h4 className="text-lg font-semibold text-teal-600">Why so many data processing engines?</h4>
                <i className={`ri-arrow-${activeQuestion === 1 ? 'up' : 'down'}-s-line text-2xl text-gray-400`}></i>
              </button>
              {activeQuestion === 1 && (
                <div className="px-6 pb-4">
                  <p className="text-gray-700">
                    Although most dataframe API tries to mimic Pandas there are always little differences in the way these dataframes work. With Optimus, we want to let you code and then use the technology and infrastructure available to you to process your data.
                  </p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg overflow-hidden">
              <button
                onClick={() => toggleQuestion(2)}
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <h4 className="text-lg font-semibold text-teal-600">How can Optimus use CPUs and GPUs?</h4>
                <i className={`ri-arrow-${activeQuestion === 2 ? 'up' : 'down'}-s-line text-2xl text-gray-400`}></i>
              </button>
              {activeQuestion === 2 && (
                <div className="px-6 pb-4">
                  <p className="text-gray-700">
                    For CPU, Optimus can use Pandas, Dask, Spark, or Vaex. For GPUs, Optimus relies on cuDF and Dask-cuDF.
                  </p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg overflow-hidden">
              <button
                onClick={() => toggleQuestion(3)}
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <h4 className="text-lg font-semibold text-teal-600">Not sure if I understand between Pandas, Dask, cuDF... and Optimus. Can you explain further?</h4>
                <i className={`ri-arrow-${activeQuestion === 3 ? 'up' : 'down'}-s-line text-2xl text-gray-400`}></i>
              </button>
              {activeQuestion === 3 && (
                <div className="px-6 pb-4">
                  <p className="text-gray-700">
                    Optimus focused on give you the best tools for all your data processing needs. From data quality, plotting, parsing dates, URLs, email, and NLP preparation. Optimus give you the best performance, so you don't have to reinvent the wheel.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <h3 className="text-4xl font-bold text-gray-900 mb-4">Join Our our not disturbing Newsletter</h3>
          <p className="text-lg text-gray-700 mb-8">Want to know about new releases and how you can help Optimus?</p>
          
          <form 
            id="newsletter-form"
            data-readdy-form
            onSubmit={handleNewsletterSubmit} 
            className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto"
          >
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-3 bg-white border-2 border-gray-900 text-gray-900 font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
            >
              {isSubmitting ? 'Subscribing...' : 'Subscribe'}
            </button>
          </form>
          
          {submitMessage && (
            <p className="mt-4 text-teal-600 font-medium">{submitMessage}</p>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-gray-400">© 2025 All rights reserved.</p>
            
            <div className="flex items-center gap-6">
              <a href="https://twitter.com/hi_optimus" target="_blank" rel="noopener noreferrer nofollow" className="text-gray-400 hover:text-white transition-colors cursor-pointer" aria-label="Twitter">
                <i className="ri-twitter-x-line text-xl"></i>
              </a>
              <a href="https://medium.com/hi-optimus" target="_blank" rel="noopener noreferrer nofollow" className="text-gray-400 hover:text-white transition-colors cursor-pointer" aria-label="Medium">
                <i className="ri-medium-line text-xl"></i>
              </a>
              <a href="https://github.com/ironmussa/Optimus" target="_blank" rel="noopener noreferrer nofollow" className="text-gray-400 hover:text-white transition-colors cursor-pointer" aria-label="GitHub">
                <i className="ri-github-line text-xl"></i>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
